/**
 * A CAPTCHA token for the anonymous sign-in, when the project asks for one.
 *
 * Anonymous identities are what the write path counts: the function's hourly
 * limit and the `user_id` it stamps are both per caller, and both are worth
 * exactly as much as minting a caller costs. A CAPTCHA is what puts a price
 * on it.
 *
 * **Supabase does the verifying, not this game.** The token is handed to
 * `signInAnonymously`, and Supabase Auth calls Cloudflare's `siteverify` with
 * the secret key held in its own dashboard. That is why nothing here — and
 * nothing in `supabase/functions/` — ever sees the secret: adding our own
 * verification would mean storing it in a second place for no gain.
 *
 * Inert until `VITE_TURNSTILE_SITE_KEY` is set, so the game runs unchanged
 * with no key. Failing to get a token is not fatal here: whether one was
 * required is the server's call, and a blocked widget must not be able to
 * take the board down on its own.
 */

const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Reported to Cloudflare's dashboard so the widget's traffic is legible. */
const ACTION = "anonymous-signin";

/**
 * How long to wait before giving up on a token.
 *
 * A widget that never calls back would otherwise hold the submission open
 * forever, and the run being saved is what the player is waiting on.
 */
const TOKEN_TIMEOUT_MS = 20_000;

interface RenderOptions {
  sitekey: string;
  action?: string;
  callback: (token: string) => void;
  "error-callback": () => void;
  "timeout-callback"?: () => void;
  "before-interactive-callback"?: () => void;
}

interface Turnstile {
  render(container: HTMLElement, options: RenderOptions): string | undefined;
  remove(widget: string): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

export interface CaptchaOptions {
  /** Overrides the build-time key; tests pass one in. */
  readonly siteKey?: string;
  /** Swappable so a test never reaches the network. */
  readonly loadScript?: () => Promise<Turnstile | undefined>;
  readonly timeoutMs?: number;
}

function configuredKey(): string | undefined {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  return key && key.length > 0 ? key : undefined;
}

let scriptLoad: Promise<Turnstile | undefined> | null = null;

/** Loaded once, and only if a key is configured — never on a cold start. */
function loadTurnstile(): Promise<Turnstile | undefined> {
  if (typeof document === "undefined") {
    return Promise.resolve(undefined);
  }
  scriptLoad ??= new Promise<Turnstile | undefined>((resolve) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      // Let a later attempt try again: a blocked or flaky load should not
      // disable the board for the rest of the session.
      scriptLoad = null;
      resolve(undefined);
    };
    document.head.append(script);
  });
  return scriptLoad;
}

/**
 * The host for the widget.
 *
 * Off-screen rather than `display: none`, because a widget in a hidden
 * container cannot be solved and a Managed widget may decide it needs the
 * player to do something. `before-interactive-callback` is what brings it
 * into view for that case — the common path never shows anything.
 */
function createContainer(): HTMLElement {
  const container = document.createElement("div");
  container.className = "captcha";
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.style.zIndex = "40";
  document.body.append(container);
  return container;
}

function reveal(container: HTMLElement): void {
  container.style.left = "50%";
  container.style.top = "50%";
  container.style.transform = "translate(-50%, -50%)";
}

/**
 * A fresh token, or undefined when there is nothing to ask or nothing came
 * back. Turnstile tokens are single use, so a widget is rendered per sign-in
 * rather than one being cached.
 */
export async function getCaptchaToken(
  options: CaptchaOptions = {},
): Promise<string | undefined> {
  const siteKey = options.siteKey ?? configuredKey();
  if (!siteKey) {
    return undefined;
  }

  const turnstile = await (options.loadScript ?? loadTurnstile)();
  if (!turnstile || typeof document === "undefined") {
    return undefined;
  }

  const container = createContainer();

  return new Promise<string | undefined>((resolve) => {
    let widget: string | undefined;
    let settled = false;
    const timer = setTimeout(
      () => finish(undefined),
      options.timeoutMs ?? TOKEN_TIMEOUT_MS,
    );

    function finish(token: string | undefined): void {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      if (widget !== undefined) {
        turnstile!.remove(widget);
      }
      container.remove();
      resolve(token);
    }

    try {
      widget = turnstile.render(container, {
        sitekey: siteKey,
        action: ACTION,
        callback: (token) => finish(token),
        "error-callback": () => finish(undefined),
        "timeout-callback": () => finish(undefined),
        "before-interactive-callback": () => reveal(container),
      });
      if (widget === undefined) {
        finish(undefined);
      }
    } catch {
      finish(undefined);
    }
  });
}
